import {colors} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled, {createGlobalStyle} from 'styled-components'

import Header from '@/components/header'
import {PreLaunchForm} from '@/components/lead-form/pre-launch-form'
import SEO from '@/components/seo'
import type {PreLaunchFormPageQuery} from '@/graphql-types'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import {useLocale} from '@helios/shared/providers/locale'
import {container} from '@helios/shared/styles/grid'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<PreLaunchFormPageQuery>

const PreLaunchFormPage = (props: Props) => {
    const content = props.data.prismicPreLaunchFormPage.data
    const locale = useLocale()

    return (
        <>
            <GlobalStyles />

            <HeaderWrapper>
                <Header hideLeftNav hideRightNav />
            </HeaderWrapper>

            <Main>
                <MainSection>
                    <PreLaunchForm
                        title={content.form_title}
                        submitText={content.submit_text}
                        confirmationPage={content.confirmation_page}
                        hubspotProperties={{
                            campaign__updating_: content.hubspot_properties[0].campaign,
                            locale: locale.urlCode,
                        }}
                    />
                </MainSection>
            </Main>
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<PreLaunchFormPageQuery>) => {
    const pageData = data.prismicPreLaunchFormPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
            preventIndexing={pageData.exclude_robots}
        />
    )
}

const mobileStylesBreakpoint = 'xs'

const GlobalStyles = createGlobalStyle`
    body {
        ${mq.min(mobileStylesBreakpoint)} {
            background: ${colors.background.muted};
        }
    }
`

const HeaderWrapper = styled.div`
    header {
        ${mq.min(mobileStylesBreakpoint)} {
            background: transparent;
        }
    }
`

const MainSection = styled.section`
    display: flex;
    justify-content: center;
    padding-bottom: 80px;

    ${mq.min(mobileStylesBreakpoint)} {
        ${container}
    }
`

export const query = graphql`
    query PreLaunchFormPage($locale: String!) {
        prismicPreLaunchFormPage(lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                exclude_robots
                open_graph_image {
                    url
                }
                form_title
                submit_text
                hubspot_properties {
                    campaign
                }
                confirmation_page {
                    ...PrismicLink
                }
            }
        }
    }
`

export default withPrismicPreview(PreLaunchFormPage)
